import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Send } from "lucide-react";

interface TestSendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flowId: number | null;
}

export default function TestSendDialog({ open, onOpenChange, flowId }: TestSendDialogProps) {
  const [tab, setTab] = useState<"contact" | "phone">("phone");
  const [contactSearch, setContactSearch] = useState("");
  const [contactId, setContactId] = useState<number | null>(null);
  const [phone, setPhone] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const contactsQuery = trpc.contacts.list.useQuery(
    { search: contactSearch, limit: 20 },
    { enabled: open && tab === "contact" },
  );

  const sendMutation = trpc.flows.testSendToNumber.useMutation({
    onSuccess: (data) => {
      toast.success(`Envio teste disparado (execução #${data.executionId}). Verifique o WhatsApp do destinatário.`);
      reset();
      onOpenChange(false);
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao enviar teste")),
  });

  const reset = () => {
    setContactId(null);
    setPhone("");
    setContactSearch("");
    setConfirmed(false);
  };

  const handleSend = () => {
    if (!flowId) return;
    if (tab === "contact") {
      if (!contactId) {
        toast.error("Selecione um contato.");
        return;
      }
      sendMutation.mutate({ flowId, contactId });
    } else {
      if (!phone.trim()) {
        toast.error("Digite um número de telefone.");
        return;
      }
      sendMutation.mutate({ flowId, phone: phone.trim() });
    }
  };

  const canSend =
    confirmed &&
    !sendMutation.isPending &&
    (tab === "contact" ? !!contactId : phone.trim().length >= 10);

  const contactList = (contactsQuery.data as any)?.contacts ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-4 h-4 text-orange-600" />
            Envio teste do fluxo
          </DialogTitle>
          <DialogDescription>
            Dispara o fluxo real para um único número antes do broadcast.
          </DialogDescription>
        </DialogHeader>

        {/* Aviso bem destacado */}
        <div className="flex gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-900 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-red-600" />
          <div>
            <strong>Este envio é REAL.</strong> A mensagem será entregue pelo WhatsApp para o número escolhido. Use seu próprio número para testar.
          </div>
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as "contact" | "phone")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="phone">Novo número</TabsTrigger>
            <TabsTrigger value="contact">Contato existente</TabsTrigger>
          </TabsList>

          <TabsContent value="phone" className="space-y-2 pt-3">
            <Label htmlFor="phone-input" className="text-sm">
              Número (com DDI + DDD)
            </Label>
            <Input
              id="phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Ex: 5566999999999"
              inputMode="numeric"
            />
            <p className="text-xs text-muted-foreground">
              Pode digitar com espaços/parênteses — o sistema normaliza.
            </p>
          </TabsContent>

          <TabsContent value="contact" className="space-y-2 pt-3">
            <Label htmlFor="contact-search" className="text-sm">
              Buscar contato
            </Label>
            <Input
              id="contact-search"
              value={contactSearch}
              onChange={(e) => {
                setContactSearch(e.target.value);
                setContactId(null);
              }}
              placeholder="Nome, telefone, email..."
            />
            <div className="max-h-44 overflow-y-auto border rounded-md divide-y">
              {contactList.length === 0 && (
                <div className="p-3 text-xs text-muted-foreground text-center">
                  {contactsQuery.isPending ? "Carregando..." : "Nenhum contato encontrado."}
                </div>
              )}
              {contactList.map((c: any) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setContactId(c.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/60 ${
                    contactId === c.id ? "bg-primary/10" : ""
                  }`}
                >
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.phoneNumber}</div>
                </button>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex items-start gap-2 pt-1">
          <Checkbox
            id="confirm-send"
            checked={confirmed}
            onCheckedChange={(v) => setConfirmed(!!v)}
          />
          <Label htmlFor="confirm-send" className="text-sm leading-tight cursor-pointer">
            Entendi que será enviado de verdade pelo WhatsApp para o destinatário.
          </Label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sendMutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={!canSend} className="gap-1.5">
            <Send className="w-4 h-4" />
            {sendMutation.isPending ? "Enviando..." : "Enviar teste"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
